/**
 * Shared sorting utilities for DataGrid (v8) and DataGridV9.
 *
 * Provides type-aware comparison of sort keys across string, number, bigint, Date, and boolean types.
 * Designed to be used standalone with v9 native `createTableColumn({ compare })` or inside custom sort logic.
 */

export type DataGridSortKey = string | number | bigint | Date | boolean | null | undefined;

/**
 * Returns true if a sort key should be treated as "empty" for sorting purposes.
 * Empty values are: null, undefined, NaN, empty string, and "-" (common placeholder).
 */
export function isEmptySortKey(key: DataGridSortKey): boolean {
    return key == null || key === "" || key === "-" || (typeof key === "number" && isNaN(key));
}

const enum SortKeyType {
    text = 1,
    numeric = 2,
    bigint = 3,
    date = 4,
    bool = 5,
    unsorted = 6,
}

function getSortKeyType(a: DataGridSortKey): SortKeyType {
    if (typeof a === "string") return SortKeyType.text;
    if (typeof a === "number") return SortKeyType.numeric;
    if (typeof a === "bigint") return SortKeyType.bigint;
    if (a instanceof Date) return SortKeyType.date;
    if (typeof a === "boolean") return SortKeyType.bool;
    return SortKeyType.unsorted;
}

function compareKeys(a: DataGridSortKey, b: DataGridSortKey, typeA: SortKeyType, typeB: SortKeyType): number {
    if (typeA === typeB) {
        if (typeA === SortKeyType.text) return (a as string).localeCompare(b as string);
        if (typeA === SortKeyType.numeric) {
            const na = a as number, nb = b as number;
            return isNaN(na) ? 1 : isNaN(nb) ? -1 : na - nb;
        }
        if (typeA === SortKeyType.bigint) {
            const diff = (a as bigint) - (b as bigint);
            return diff > 0n ? 1 : diff < 0n ? -1 : 0;
        }
        if (typeA === SortKeyType.date) return (a as Date).getTime() - (b as Date).getTime();
        if (typeA === SortKeyType.bool) return a ? -1 : b ? 1 : 0;
    }
    return typeA - typeB;
}

/**
 * Compares two DataGridSortKey values with type-aware logic.
 * Null/undefined are treated as the smallest value (sort to end in ascending).
 *
 * Note: v9 DataGrid inverts the compare result for descending sort, which moves
 * nulls to the top. Use `nullsLastCompare` wrapper in column definitions to
 * keep nulls at the bottom regardless of sort direction.
 */
export function compareSortKeys(a: DataGridSortKey, b: DataGridSortKey): number {
    const typeA = getSortKeyType(a);
    const typeB = getSortKeyType(b);
    return compareKeys(a, b, typeA, typeB);
}

/**
 * Sorts an array of items using a getSortKey extractor or customCompare function.
 * Null/undefined sort keys are treated as the smallest value.
 *
 * @param items - The items to sort
 * @param getSortKey - Extractor function to get the sort key from an item
 * @param isSortedDescending - Whether to sort in descending order
 * @param customCompare - Optional custom compare function (bypasses getSortKey)
 * @returns A new sorted array
 */
export function sortItems<TItem>(
    items: TItem[],
    getSortKey: ((item: TItem) => DataGridSortKey) | undefined,
    isSortedDescending: boolean,
    customCompare?: (a: TItem, b: TItem) => number
): TItem[] {
    if (!items?.length || items.length < 2) return items;

    const sign = isSortedDescending ? -1 : 1;

    if (customCompare) {
        return items.slice().sort((a, b) => sign * customCompare(a, b));
    }

    if (!getSortKey) return items;

    return items
        .map(item => {
            const sortKey = getSortKey(item);
            return { item, sortKey, keyType: getSortKeyType(sortKey) };
        })
        .sort((a, b) => {
            if (a.keyType === SortKeyType.unsorted && b.keyType !== SortKeyType.unsorted) return -1 * sign;
            if (b.keyType === SortKeyType.unsorted && a.keyType !== SortKeyType.unsorted) return 1 * sign;
            return sign * compareKeys(a.sortKey, b.sortKey, a.keyType, b.keyType);
        })
        .map(x => x.item);
}
